/**
 * Transient UI state for the Drafts feature (IR plan §6.5 working buffer).
 *
 * **Server data goes in React Query, never here.** Snapshots, lineage,
 * DRC findings, and IR JSON all live under `circuitKeys` and are read
 * via the `useSnapshot* / useVersion*` hooks. This store only holds:
 *
 *   * which snapshot the Drafts panel is currently focused on
 *   * which DRC severity tab is active
 *   * whether the panel is open
 *
 * `activeSnapshotId` is intentionally NOT persisted — snapshots are
 * working-buffer state with their own GC; persisting a stale id across
 * sessions risks pointing at a GC'd record.
 */
import { create } from "zustand";

import type { DrcSeverity } from "@models/circuit-ir";

type DrcFilter = DrcSeverity | "all";

/**
 * Three-state panel mode replacing the original boolean `panelOpen`.
 * The collapse-to-icon variant is the IR plan §4 / W3 ask — the panel
 * never fully disappears, it just minimises to a 44px icon column on
 * the right edge so the user can re-open it without having to discover
 * a snapshot pill in chat history.
 */
export type DraftPanelMode = "open" | "collapsed" | "closed";

export type DraftPanelTab = "findings" | "lineage" | "inspect" | "bom";

interface DraftState {
  /** Replaces `panelOpen`. `panelOpen` is kept as a derived getter
   *  below for back-compat with the existing rack pulse code. */
  panelMode: DraftPanelMode;
  /** Sheet width in px — clamped to [288, 640] by the resize handler. */
  panelWidth: number;
  /** Active tab inside the sheet. */
  panelTab: DraftPanelTab;
  activeSnapshotId: string | null;
  drcFilter: DrcFilter;
}

interface DraftActions {
  openPanel: (snapshotId: string) => void;
  collapsePanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setActiveSnapshot: (snapshotId: string | null) => void;
  setDrcFilter: (filter: DrcFilter) => void;
  setPanelTab: (tab: DraftPanelTab) => void;
  setPanelWidth: (width: number) => void;
}

const MIN_PANEL_WIDTH = 288;
const MAX_PANEL_WIDTH = 640;

export const useDraftStore = create<
  DraftState & DraftActions & { panelOpen: boolean }
>()((set, get) => ({
  panelMode: "closed",
  panelWidth: 384,
  panelTab: "findings",
  activeSnapshotId: null,
  drcFilter: "all",
  /** Derived back-compat getter — `panelOpen` is true iff the sheet
   *  is in `open` mode. Lets rack-pulse + other consumers check a
   *  single flag without learning about modes. */
  get panelOpen() {
    return get().panelMode === "open";
  },
  openPanel: (snapshotId) =>
    set({ panelMode: "open", activeSnapshotId: snapshotId }),
  collapsePanel: () => set({ panelMode: "collapsed" }),
  closePanel: () => set({ panelMode: "closed" }),
  togglePanel: () =>
    set((s) => ({
      panelMode: s.panelMode === "open" ? "collapsed" : "open",
    })),
  setActiveSnapshot: (snapshotId) => set({ activeSnapshotId: snapshotId }),
  setDrcFilter: (filter) => set({ drcFilter: filter }),
  setPanelTab: (tab) => set({ panelTab: tab }),
  setPanelWidth: (width) =>
    set({
      panelWidth: Math.max(
        MIN_PANEL_WIDTH,
        Math.min(MAX_PANEL_WIDTH, Math.round(width)),
      ),
    }),
}));

export const DRAFT_PANEL_MIN = MIN_PANEL_WIDTH;
export const DRAFT_PANEL_MAX = MAX_PANEL_WIDTH;
